import { useEffect, useState, useCallback } from "react";
import { Truck, Plus, Trash, UserCircle, Buildings, MapTrifold, ChartLineUp, Gauge } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { COUNTRIES } from "@/lib/countries";
import { weightLabel, distanceLabel, weightUnit } from "@/lib/units";

const BLANK_VEHICLE = { registration: "", vehicle_type: "van", capacity_kg: 1000, depot_id: "", status: "available", odometer_km: 0, driver_id: "", notes: "" };
const BLANK_DRIVER = { name: "", phone: "", licence_no: "", licence_expiry: "", status: "available", vehicle_id: "", notes: "" };
const BLANK_DEPOT = { name: "", address: "", city: "", country: "US", dock_count: 1, notes: "" };
const BLANK_TRIP = { name: "", trip_date: new Date().toISOString().slice(0, 10), vehicle_id: "", driver_id: "", shipment_ids: [], notes: "" };

export default function Logistics() {
  const { user, refresh } = useAuth();
  const units = user?.unit_system || "metric";
  const [view, setView] = useState("dispatch");
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [depots, setDepots] = useState([]);
  const [trips, setTrips] = useState([]);
  const [board, setBoard] = useState({ unassigned: [], assigned: [], fleet: {} });
  const [kpis, setKpis] = useState(null);
  const [dialog, setDialog] = useState(null); // vehicle | driver | depot | trip | assign
  const [vForm, setVForm] = useState({ ...BLANK_VEHICLE });
  const [dForm, setDForm] = useState({ ...BLANK_DRIVER });
  const [depForm, setDepForm] = useState({ ...BLANK_DEPOT });
  const [tForm, setTForm] = useState({ ...BLANK_TRIP });
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignForm, setAssignForm] = useState({ vehicle_id: "", driver_id: "" });

  const load = useCallback(async () => {
    const [v, d, dep, t, b, k] = await Promise.all([
      api.get("/vehicles"), api.get("/drivers"), api.get("/depots"),
      api.get("/trips"), api.get("/logistics/dispatch"), api.get("/logistics/kpis"),
    ]);
    setVehicles(v.data); setDrivers(d.data); setDepots(dep.data);
    setTrips(t.data); setBoard(b.data); setKpis(k.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (path, body, resetter, blank) => {
    try {
      await api.post(path, body);
      toast.success("Saved");
      setDialog(null);
      resetter({ ...blank });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (path) => {
    try {
      await api.delete(path);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const assign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/shipments/${assignTarget.id}/assign`, {
        vehicle_id: assignForm.vehicle_id || null,
        driver_id: assignForm.driver_id || null,
      });
      toast.success("Dispatched");
      setDialog(null);
      setAssignTarget(null);
      setAssignForm({ vehicle_id: "", driver_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const runTrip = async (t, status) => {
    try {
      await api.patch(`/trips/${t.id}/status`, null, { params: { status } });
      toast.success(`Trip ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setUnits = async (system) => {
    try {
      await api.patch("/me", { unit_system: system });
      toast.success(system === "imperial" ? "Showing pounds and miles" : "Showing kilos and kilometres");
      refresh?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="logistics-page">
      <PageHeader
        overline="Operations"
        title="Logistics Management"
        description="Your fleet, drivers and depots in one place — dispatch every booking to a vehicle and driver, plan multi-stop trips against capacity, and watch on-time, cost and utilisation."
        testid="logistics-header"
        action={
          <div className="flex flex-wrap gap-2">
            <div className="flex" data-testid="units-toggle">
              {[["metric", "kg / km"], ["imperial", "lb / mi"]].map(([value, label]) => (
                <button
                  key={value}
                  data-testid={`units-${value}`}
                  onClick={() => setUnits(value)}
                  className={`border px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                    units === value ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-slate-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Button data-testid="add-vehicle-button" onClick={() => { setVForm({ ...BLANK_VEHICLE }); setDialog("vehicle"); }} className="w-full sm:w-auto">
              <Plus size={16} className="mr-1.5" /> Vehicle
            </Button>
            <Button data-testid="add-driver-button" variant="outline" onClick={() => { setDForm({ ...BLANK_DRIVER }); setDialog("driver"); }} className="w-full sm:w-auto">
              <UserCircle size={16} className="mr-1.5" /> Driver
            </Button>
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Vehicles" value={board.fleet?.vehicles || 0} sub={`${board.fleet?.available_vehicles || 0} available`} icon={Truck} testid="stat-vehicles" />
        <StatCard label="Drivers" value={board.fleet?.drivers || 0} sub={`${board.fleet?.available_drivers || 0} available`} icon={UserCircle} testid="stat-drivers" />
        <StatCard label="To dispatch" value={board.unassigned?.length || 0} tone={board.unassigned?.length ? "danger" : "default"} icon={MapTrifold} testid="stat-to-dispatch" />
        <StatCard label="On-time" value={kpis?.on_time_pct != null ? `${kpis.on_time_pct}%` : "—"} tone="primary" icon={Gauge} testid="stat-on-time" />
      </div>

      <Tabs value={view} onValueChange={setView} className="mb-4 w-full overflow-x-auto">
        <TabsList data-testid="logistics-tabs" className="w-max">
          <TabsTrigger value="dispatch" data-testid="tab-dispatch">Dispatch</TabsTrigger>
          <TabsTrigger value="fleet" data-testid="tab-fleet">Fleet</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers">Drivers</TabsTrigger>
          <TabsTrigger value="depots" data-testid="tab-depots">Depots</TabsTrigger>
          <TabsTrigger value="trips" data-testid="tab-trips">Trips</TabsTrigger>
          <TabsTrigger value="kpis" data-testid="tab-kpis">KPIs</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "dispatch" && (
        <div className="grid gap-4 lg:grid-cols-2">
          {[["To dispatch", board.unassigned, true], ["On the road", board.assigned, false]].map(([title, rows, canAssign]) => (
            <div key={title} className="border border-border bg-white" data-testid={`board-${canAssign ? "unassigned" : "assigned"}`}>
              <div className="border-b border-border px-4 py-3 font-display text-sm font-bold">{title.toUpperCase()} ({rows?.length || 0})</div>
              {!rows?.length ? (
                <p className="px-4 py-4 text-sm text-slate-500">Nothing here right now.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {rows.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid={`board-row-${s.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">{s.shipment_number} · {s.description}</div>
                        <div className="text-xs text-slate-500">
                          {s.pickup_city || s.pickup_country} → {s.dropoff_city || s.dropoff_country} ·{" "}
                          {weightLabel(s.weight_kg, units)}
                          {s.distance_km ? ` · ${distanceLabel(s.distance_km, units)}` : ""}
                        </div>
                        {(s.vehicle_registration || s.driver_name) && (
                          <div className="overline text-[0.6rem] text-[#002FA7]">
                            {s.vehicle_registration} {s.driver_name}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge value={s.status} />
                        {canAssign && (
                          <Button size="sm" data-testid={`assign-${s.id}`} onClick={() => { setAssignTarget(s); setAssignForm({ vehicle_id: "", driver_id: "" }); setDialog("assign"); }}>
                            Assign
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {view === "fleet" && (
        vehicles.length === 0 ? (
          <EmptyState title="No vehicles yet" description="Add your vans, trucks or carts with their capacity." testid="fleet-empty" />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="fleet-list">
            {vehicles.map((v) => (
              <div key={v.id} className="border border-border bg-white p-4" data-testid={`vehicle-card-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-base font-semibold">{v.registration}</div>
                    <div className="overline text-[0.6rem] text-slate-500">{v.vehicle_type} · {v.owner_name}</div>
                  </div>
                  <StatusBadge value={v.status === "available" ? "active" : v.status} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-y-1 text-xs">
                  <div><dt className="text-slate-400">Capacity</dt><dd className="font-semibold">{weightLabel(v.capacity_kg, units)}</dd></div>
                  <div><dt className="text-slate-400">Odometer</dt><dd className="font-semibold">{distanceLabel(v.odometer_km, units)}</dd></div>
                  <div><dt className="text-slate-400">Depot</dt><dd className="font-semibold">{v.depot_name || "—"}</dd></div>
                  <div><dt className="text-slate-400">Driver</dt><dd className="font-semibold">{v.driver_name || "—"}</dd></div>
                </dl>
                <Button size="sm" variant="ghost" className="mt-3" data-testid={`delete-vehicle-${v.id}`} onClick={() => remove(`/vehicles/${v.id}`)}>
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        )
      )}

      {view === "drivers" && (
        drivers.length === 0 ? (
          <EmptyState title="No drivers yet" description="Add your drivers with licence details so you can dispatch them." testid="drivers-empty" />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="drivers-list">
            {drivers.map((d) => (
              <div key={d.id} className="border border-border bg-white p-4" data-testid={`driver-card-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-base font-semibold">{d.name}</div>
                    <div className="overline text-[0.6rem] text-slate-500">{d.phone || "no phone"} · {d.owner_name}</div>
                  </div>
                  <StatusBadge value={d.status === "available" ? "active" : d.status} />
                </div>
                <div className="mt-2 text-xs text-slate-600">
                  Licence {d.licence_no || "—"}{d.licence_expiry ? ` · expires ${d.licence_expiry}` : ""}
                </div>
                <Button size="sm" variant="ghost" className="mt-3" data-testid={`delete-driver-${d.id}`} onClick={() => remove(`/drivers/${d.id}`)}>
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        )
      )}

      {view === "depots" && (
        <>
          <Button size="sm" className="mb-3" data-testid="add-depot-button" onClick={() => { setDepForm({ ...BLANK_DEPOT }); setDialog("depot"); }}>
            <Buildings size={14} className="mr-1.5" /> Add depot
          </Button>
          {depots.length === 0 ? (
            <EmptyState title="No depots yet" description="Add the yards or warehouses your vehicles work out of." testid="depots-empty" />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="depots-list">
              {depots.map((d) => (
                <div key={d.id} className="border border-border bg-white p-4" data-testid={`depot-card-${d.id}`}>
                  <div className="font-display text-base font-semibold">{d.name}</div>
                  <div className="text-xs text-slate-600">{d.address}</div>
                  <div className="overline text-[0.6rem] text-slate-500">{d.city} {d.country} · {d.dock_count} dock(s)</div>
                  {user?.role === "admin" && (
                    <Button size="sm" variant="ghost" className="mt-3" data-testid={`delete-depot-${d.id}`} onClick={() => remove(`/depots/${d.id}`)}>
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === "trips" && (
        <>
          <Button size="sm" className="mb-3" data-testid="add-trip-button" onClick={() => { setTForm({ ...BLANK_TRIP }); setDialog("trip"); }}>
            <MapTrifold size={14} className="mr-1.5" /> Plan a trip
          </Button>
          {trips.length === 0 ? (
            <EmptyState title="No trips planned" description="Group several bookings into one multi-stop run against a vehicle's capacity." testid="trips-empty" />
          ) : (
            <div className="space-y-3" data-testid="trips-list">
              {trips.map((t) => (
                <div key={t.id} className="border border-border bg-white p-4" data-testid={`trip-card-${t.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-base font-semibold">{t.name}</div>
                      <div className="overline text-[0.6rem] text-slate-500">
                        {t.trip_date} · {t.vehicle_registration || "no vehicle"} · {t.driver_name || "no driver"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge value={t.status === "planned" ? "pending" : t.status === "done" ? "approved" : "active"} />
                      {t.status !== "done" && (
                        <Button size="sm" variant="outline" data-testid={`run-trip-${t.id}`} onClick={() => runTrip(t, t.status === "planned" ? "running" : "done")}>
                          {t.status === "planned" ? "Start" : "Complete"}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" data-testid={`delete-trip-${t.id}`} onClick={() => remove(`/trips/${t.id}`)}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                  <div className={`mt-2 text-xs font-semibold ${t.over_capacity ? "text-[#FF3B30]" : "text-slate-600"}`} data-testid={`trip-load-${t.id}`}>
                    Load {weightLabel(t.total_weight_kg, units)} / capacity {weightLabel(t.capacity_kg, units)}
                    {t.over_capacity ? " — OVER CAPACITY" : ""}
                  </div>
                  <ol className="mt-2 space-y-1 text-xs text-slate-600">
                    {t.stops.map((s) => (
                      <li key={s.order}>
                        {s.order}. {s.shipment_number} — {s.pickup} → {s.dropoff} ({weightLabel(s.weight_kg, units)})
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === "kpis" && kpis && (
        <div className="space-y-4" data-testid="kpis-panel">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Delivered" value={kpis.delivered} icon={ChartLineUp} testid="kpi-delivered" />
            <StatCard label={`Cost per ${weightUnit(units)}`} value={kpis.cost_per_kg != null ? kpis.cost_per_kg : "—"} icon={ChartLineUp} testid="kpi-cost-per-kg" />
            <StatCard label="Cost per distance" value={kpis.cost_per_km != null ? kpis.cost_per_km : "—"} icon={ChartLineUp} testid="kpi-cost-per-km" />
            <StatCard label="Fleet utilisation" value={kpis.fleet_utilisation_pct != null ? `${kpis.fleet_utilisation_pct}%` : "—"} tone="primary" icon={Gauge} testid="kpi-utilisation" />
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Late now" value={kpis.late_now ?? 0} icon={Gauge} testid="kpi-late-now" />
            <StatCard label="Not collected" value={kpis.pickup_overdue_now ?? 0} icon={Gauge} testid="kpi-pickup-overdue" />
            <StatCard label="At risk" value={kpis.at_risk_now ?? 0} icon={Gauge} testid="kpi-at-risk" />
            <StatCard label="On-time vs promise" value={kpis.window_on_time_pct != null ? `${kpis.window_on_time_pct}%` : "—"} tone="primary" icon={Gauge} testid="kpi-window-on-time" />
          </div>
          <a href="/freight-time" data-testid="kpi-freight-time-link" className="tap-safe inline-block text-sm font-semibold text-[#002FA7]">
            Open the freight time board →
          </a>
          <div className="grid gap-4 lg:grid-cols-2">
            {[["Revenue by lane", kpis.revenue_by_lane, "lane"], ["Revenue by carrier", kpis.revenue_by_carrier, "carrier"]].map(([title, rows, key]) => (
              <div key={title} className="border border-border bg-white">
                <div className="border-b border-border px-4 py-3 font-display text-sm font-bold">{title.toUpperCase()}</div>
                {!rows?.length ? (
                  <p className="px-4 py-4 text-sm text-slate-500">No delivered bookings yet.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {rows.map((r) => (
                      <li key={r[key]} className="flex justify-between px-4 py-2.5 text-sm">
                        <span>{r[key]}</span>
                        <span className="font-mono font-semibold">{r.revenue.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vehicle dialog */}
      <Dialog open={dialog === "vehicle"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Add a vehicle</DialogTitle>
            <DialogDescription>Capacity is used to block overloaded dispatches and trips.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submit("/vehicles", { ...vForm, capacity_kg: Number(vForm.capacity_kg), odometer_km: Number(vForm.odometer_km), depot_id: vForm.depot_id || null, driver_id: vForm.driver_id || null }, setVForm, BLANK_VEHICLE); }} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Registration</Label>
                <Input data-testid="vehicle-registration-input" required value={vForm.registration} onChange={(e) => setVForm({ ...vForm, registration: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={vForm.vehicle_type} onValueChange={(v) => setVForm({ ...vForm, vehicle_type: v })}>
                  <SelectTrigger data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["cart", "van", "truck", "flatbed", "reefer", "container"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Capacity ({weightUnit(units)} shown, stored in kg)</Label>
                <Input data-testid="vehicle-capacity-input" type="number" min="1" value={vForm.capacity_kg} onChange={(e) => setVForm({ ...vForm, capacity_kg: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={vForm.status} onValueChange={(v) => setVForm({ ...vForm, status: v })}>
                  <SelectTrigger data-testid="vehicle-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="on_job">On job</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Depot</Label>
                <Select value={vForm.depot_id || "none"} onValueChange={(v) => setVForm({ ...vForm, depot_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="vehicle-depot-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {depots.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button type="submit" data-testid="submit-vehicle-button" className="w-full sm:w-auto">Add vehicle</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Driver dialog */}
      <Dialog open={dialog === "driver"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="driver-dialog">
          <DialogHeader>
            <DialogTitle>Add a driver</DialogTitle>
            <DialogDescription>Drivers you can dispatch to bookings and trips.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submit("/drivers", { ...dForm, vehicle_id: dForm.vehicle_id || null }, setDForm, BLANK_DRIVER); }} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name</Label>
                <Input data-testid="driver-name-input" required value={dForm.name} onChange={(e) => setDForm({ ...dForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="driver-phone-input" value={dForm.phone} onChange={(e) => setDForm({ ...dForm, phone: e.target.value })} />
              </div>
              <div>
                <Label>Licence no.</Label>
                <Input data-testid="driver-licence-input" value={dForm.licence_no} onChange={(e) => setDForm({ ...dForm, licence_no: e.target.value })} />
              </div>
              <div>
                <Label>Licence expiry</Label>
                <Input data-testid="driver-expiry-input" type="date" value={dForm.licence_expiry} onChange={(e) => setDForm({ ...dForm, licence_expiry: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={dForm.status} onValueChange={(v) => setDForm({ ...dForm, status: v })}>
                  <SelectTrigger data-testid="driver-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="on_job">On job</SelectItem>
                    <SelectItem value="off">Off duty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button type="submit" data-testid="submit-driver-button" className="w-full sm:w-auto">Add driver</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Depot dialog */}
      <Dialog open={dialog === "depot"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="depot-dialog">
          <DialogHeader>
            <DialogTitle>Add a depot</DialogTitle>
            <DialogDescription>Yards and warehouses your fleet works out of.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submit("/depots", { ...depForm, dock_count: Number(depForm.dock_count) }, setDepForm, BLANK_DEPOT); }} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="depot-name-input" required value={depForm.name} onChange={(e) => setDepForm({ ...depForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea data-testid="depot-address-input" rows={2} value={depForm.address} onChange={(e) => setDepForm({ ...depForm, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>City</Label>
                <Input data-testid="depot-city-input" value={depForm.city} onChange={(e) => setDepForm({ ...depForm, city: e.target.value })} />
              </div>
              <div>
                <Label>Country</Label>
                <Select value={depForm.country} onValueChange={(v) => setDepForm({ ...depForm, country: v })}>
                  <SelectTrigger data-testid="depot-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRIES.map(([code, name]) => <SelectItem key={code} value={code}>{code} — {name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Docks</Label>
                <Input data-testid="depot-docks-input" type="number" min="1" value={depForm.dock_count} onChange={(e) => setDepForm({ ...depForm, dock_count: e.target.value })} />
              </div>
            </div>
            <DialogFooter><Button type="submit" data-testid="submit-depot-button" className="w-full sm:w-auto">Add depot</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Trip dialog */}
      <Dialog open={dialog === "trip"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="trip-dialog">
          <DialogHeader>
            <DialogTitle>Plan a trip</DialogTitle>
            <DialogDescription>Pick the bookings in stop order — we total the load against the vehicle's capacity.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submit("/trips", { ...tForm, vehicle_id: tForm.vehicle_id || null, driver_id: tForm.driver_id || null }, setTForm, BLANK_TRIP); }} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Trip name</Label>
                <Input data-testid="trip-name-input" required value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} placeholder="Monday city run" />
              </div>
              <div>
                <Label>Date</Label>
                <Input data-testid="trip-date-input" type="date" required value={tForm.trip_date} onChange={(e) => setTForm({ ...tForm, trip_date: e.target.value })} />
              </div>
              <div>
                <Label>Vehicle</Label>
                <Select value={tForm.vehicle_id || "none"} onValueChange={(v) => setTForm({ ...tForm, vehicle_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="trip-vehicle-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration} ({weightLabel(v.capacity_kg, units)})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Driver</Label>
                <Select value={tForm.driver_id || "none"} onValueChange={(v) => setTForm({ ...tForm, driver_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="trip-driver-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Bookings (tap to add in stop order)</Label>
              <div className="mt-1 max-h-52 space-y-1 overflow-y-auto border border-border p-2" data-testid="trip-booking-picker">
                {[...(board.unassigned || []), ...(board.assigned || [])].map((s) => {
                  const idx = tForm.shipment_ids.indexOf(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`trip-pick-${s.id}`}
                      onClick={() => setTForm({
                        ...tForm,
                        shipment_ids: idx >= 0
                          ? tForm.shipment_ids.filter((x) => x !== s.id)
                          : [...tForm.shipment_ids, s.id],
                      })}
                      className={`flex w-full items-center justify-between gap-2 border px-2 py-1.5 text-left text-xs ${
                        idx >= 0 ? "border-[#002FA7] bg-[#EEF2FF]" : "border-border bg-white"
                      }`}
                    >
                      <span className="truncate">
                        {idx >= 0 ? `${idx + 1}. ` : ""}{s.shipment_number} · {s.pickup_city || s.pickup_country} → {s.dropoff_city || s.dropoff_country}
                      </span>
                      <span className="shrink-0 font-mono">{weightLabel(s.weight_kg, units)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <DialogFooter><Button type="submit" data-testid="submit-trip-button" className="w-full sm:w-auto">Create trip</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={dialog === "assign"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Dispatch {assignTarget?.shipment_number}</DialogTitle>
            <DialogDescription>
              Load {weightLabel(assignTarget?.weight_kg, units)} — vehicles smaller than this are rejected.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={assign} className="space-y-3">
            <div>
              <Label>Vehicle</Label>
              <Select value={assignForm.vehicle_id} onValueChange={(v) => setAssignForm({ ...assignForm, vehicle_id: v })}>
                <SelectTrigger data-testid="assign-vehicle-select"><SelectValue placeholder="Pick a vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.filter((v) => v.status !== "maintenance").map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.registration} ({weightLabel(v.capacity_kg, units)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Driver</Label>
              <Select value={assignForm.driver_id} onValueChange={(v) => setAssignForm({ ...assignForm, driver_id: v })}>
                <SelectTrigger data-testid="assign-driver-select"><SelectValue placeholder="Pick a driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.filter((d) => d.status !== "off").map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="submit" data-testid="submit-assign-button" className="w-full sm:w-auto">Dispatch</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
